import React, { useState, useEffect } from 'react';
import { 
  Wand2, 
  Laptop, 
  Sparkles, 
  Palette, 
  ShieldAlert, 
  Crosshair, 
  Tag, 
  FolderOpen, 
  History, 
  Server, 
  ArrowLeft,
  Lock,
  Compass,
  Truck
} from 'lucide-react';
import { CustomProject, ColorSwatch } from '../types/productBuilder';
import { BuilderTab } from './types';
import { Launchpad } from './tabs/Launchpad';
import { Sandbox } from './tabs/Sandbox';
import { Suggestions } from './tabs/Suggestions';
import { ProjectDNA } from './tabs/ProjectDNA';
import { Guardian } from './tabs/Guardian';
import { Radar } from './tabs/Radar';
import { Bundle } from './tabs/Bundle';
import { MyProjects } from './tabs/MyProjects';
import { Versions } from './tabs/Versions';
import { Publish } from './tabs/Publish';
import { AdminDbAudit } from './tabs/AdminDbAudit';
import { DeliveryHub } from './tabs/DeliveryHub';
import { useAuth } from '../lib/AuthContext';

interface ProductBuilderWorkspaceProps {
  selectedTemplateId?: string;
  editingProjectId?: string;
  onBackToTemplates: () => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onAddTaskToWorkSuite?: (title: string, priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW') => void;
}

const COLOR_SWATCHES: ColorSwatch[] = [
  { swatchName: 'Default Cosmic (Slate / Cyan)', primary: '#00ffff', secondary: '#090d1f', darkAccent: '#010206', textPrimary: '#ffffff' },
  { swatchName: 'Calm Twilight (Purple / Pink)', primary: '#e840f3', secondary: '#1e072b', darkAccent: '#0b0210', textPrimary: '#ffffff' },
  { swatchName: 'Vanguard Corporate (Emerald / Teal)', primary: '#10b981', secondary: '#022c22', darkAccent: '#02110c', textPrimary: '#ffffff' },
  { swatchName: 'Apex Golden (Bronze / Amber)', primary: '#e5c158', secondary: '#292005', darkAccent: '#0c0a02', textPrimary: '#ffffff' },
  { swatchName: 'Solar Flare (Orange / Slate)', primary: '#f97316', secondary: '#1e293b', darkAccent: '#0f172a', textPrimary: '#ffffff' }
];

export const ProductBuilderWorkspace: React.FC<ProductBuilderWorkspaceProps> = ({
  selectedTemplateId,
  editingProjectId,
  onBackToTemplates,
  onAddLog,
  onAddTaskToWorkSuite
}) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const idToken = user ? await user.getIdToken() : '';
        const res = await fetch('/api/product-builder/user-role', {
          headers: {
            'Authorization': idToken ? `Bearer ${idToken}` : '',
            'X-CSRF-Token': document.cookie.match(/(^|;)\s*csrf_token_secret\s*=\s*([^;]+)/)?.[2] || ''
          }
        });
        const data = await res.json();
        if (data.success && data.role === 'admin') {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch (e) {
        console.error("Failed to fetch user role", e);
        setIsAdmin(false);
      }
    };
    if (user?.uid) {
      fetchUserRole();
    } else {
      setIsAdmin(false);
    }
  }, [user?.uid, user?.email]);

  // State holding current project under edit
  const [project, setProject] = useState<CustomProject | null>(null);

  // Active tab in our fully isolated internal sidebar menu (Req says: "Do not make these tabs part of the main website navigation")
  const [activeTab, setActiveTab] = useState<BuilderTab>('launchpad');

  const [saving, setSaving] = useState(false);
  const [showSetupConfirm, setShowSetupConfirm] = useState(false);

  // Load from template or load from project id
  useEffect(() => {
    onAddLog('Product Builder', 'Initializing Workspace Sandbox Contours', 'SUCCESS', 'Isolated navigation context loaded.');
    
    const initWorkspace = async () => {
      // Unauthenticated public guest demo loading logic:
      if (!user) {
        const savedGuest = localStorage.getItem('guest_temporary_project');
        if (savedGuest) {
          try {
            setProject(JSON.parse(savedGuest));
            setActiveTab('sandbox');
            onAddLog('Product Builder', 'Loaded isolated guest demo from browser cache', 'SUCCESS', 'Isolated preview environment active.');
            return;
          } catch (e) {
            console.error("Failed to parse cached guest project", e);
          }
        }
      }

      if (editingProjectId && user) {
        try {
          const idToken = await user.getIdToken();
          const res = await fetch(`/api/product-builder/projects/${editingProjectId}`, {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          });
          const data = await res.json();
          if (data.success && data.project) {
            setProject(data.project);
            setActiveTab('sandbox');
            onAddLog('Product Builder', `Restored existing draft: "${data.project.name}" from server`, 'SUCCESS', 'Loaded customized model details.');
            return;
          }
        } catch (err) {
          console.error("Failed to load project from server", err);
        }
      }

      if (selectedTemplateId) {
        const tplName = selectedTemplateId === 'ndp-1' ? 'AI Business Starter' :
                        selectedTemplateId === 'ndp-2' ? 'E-commerce Master' :
                        selectedTemplateId === 'ndp-3' ? 'SaaS Dashboard Console' : 'Custom Platform';
        
        const newProj: CustomProject = {
          id: 'proj_' + Date.now(),
          name: `Custom ${tplName}`,
          templateId: selectedTemplateId,
          industry: 'Enterprise Tech Solutions',
          projectType: 'B2B Responsive Layout Hub',
          status: 'Draft',
          pages: [
            { id: 'tpl_p1', title: 'Main Showcase Portal', url: '/', layoutSections: ['Atmospheric Header', 'Interactive Price Matrices', 'Secure Client Vault'] }
          ],
          colors: COLOR_SWATCHES[0],
          typography: 'Space Grotesk',
          createdAt: new Date().toISOString()
        };

        if (user) {
          try {
            const idToken = await user.getIdToken();
            await fetch('/api/product-builder/projects', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              body: JSON.stringify(newProj)
            });
          } catch (e) {
            console.error("Failed to pre-save template project:", e);
          }
        } else {
          localStorage.setItem('guest_temporary_project', JSON.stringify(newProj));
        }

        setProject(newProj);
        setActiveTab('sandbox');
        onAddLog('Product Builder', `Instantiated skeleton from catalog template: "${tplName}"`, 'SUCCESS', 'Created new sandbox context.');
      }
    };

    initWorkspace();
  }, [selectedTemplateId, editingProjectId, user]);

  const saveDraft = async () => {
    if (!project) return;
    setSaving(true);

    if (!user) {
      localStorage.setItem('guest_temporary_project', JSON.stringify(project));
      onAddLog('Product Builder', `Saved guest draft "${project.name}" in local storage`, 'SUCCESS', 'Isolated preview environment active.');
      alert(`PROJECT DRAFT SAVED LOCALLY!\n\nGuest Mode: Since there is no active session, your configurations have been saved locally in your browser cache.\n\nPlease sign in or authenticate to persist progress permanently to the cloud server.`);
      setSaving(false);
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const listRes = await fetch('/api/product-builder/projects', {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      const listData = await listRes.json();
      const exists = listData.success && listData.projects?.some((p: any) => p.id === project.id);

      let saveUrl = '/api/product-builder/projects';
      let method = 'POST';
      if (exists) {
        saveUrl = `/api/product-builder/projects/${project.id}`;
        method = 'PATCH';
      }

      const res = await fetch(saveUrl, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(project)
      });
      const data = await res.json();
      if (data.success) {
        setProject(data.project);
        onAddLog('Product Builder', `Saved project blueprint "${project.name}" on cloud storage`, 'SUCCESS', 'Persisted draft revisions server-side.');
        alert(`PROJECT DRAFT SAVED SUCCESSFULLY!\n\nYour configurations have been securely persisted on the cloud server.`);
      } else {
        alert(`Save failed: ${data.error || 'Server error'}`);
      }
    } catch (err: any) {
      console.error(err);
      alert(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const dispatchSetupRequest = async () => {
    if (!project) return;

    if (!user) {
      alert("UNAUTHORIZED: You must sign in or register to dispatch setup requests for manual review under production databases.");
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`/api/product-builder/projects/${project.id}/request-publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });
      const data = await res.json();
      if (data.success) {
        setProject(data.project);
        onAddLog('Product Builder', `Dispatched manual setup review sequence for "${project.name}"`, 'PENDING_APPROVAL', 'Creating PM record on Control Plane.');

        // Add task to task board simulated storage for WorkSuite
        const ticketId = `NX-SET-${Math.floor(Math.random() * 90000 + 10000)}`;
        const taskTitle = `DEPLOY SKELETON [${ticketId}]: "${project.name}" - Customizer draft setup manual review`;
        if (onAddTaskToWorkSuite) {
          onAddTaskToWorkSuite(taskTitle, 'URGENT');
        } else {
          const stored = localStorage.getItem('nexorax_control_plane_tasks');
          const taskList = stored ? JSON.parse(stored) : [];
          taskList.unshift({
            id: `task-${Date.now()}`,
            title: taskTitle,
            project: 'Digital Setup',
            client: user?.displayName || 'NexoraX Client Representative',
            priority: 'URGENT',
            status: 'TODO'
          });
          localStorage.setItem('nexorax_control_plane_tasks', JSON.stringify(taskList));
        }

        setShowSetupConfirm(true);
        onAddLog('WorkSuite Core', 'Dispatched physical zero-fund setup records', 'SUCCESS', 'Manual queue slot secured.');
      } else {
        alert(`Dispatch failed: ${data.error || 'Server error'}`);
      }
    } catch (err: any) {
      alert(`Dispatch failed: ${err.message}`);
    }
  };

  const menuItems: { id: BuilderTab; label: string; icon: React.FC<any>; badge?: string }[] = [
    { id: 'launchpad', label: 'Launchpad Entrance', icon: Wand2 },
    { id: 'sandbox', label: 'Galaxy Sandbox', icon: Laptop, badge: project ? 'Draft' : undefined },
    { id: 'deliveryHub', label: 'Delivery & Driver Operations', icon: Truck, badge: 'New' },
    { id: 'suggestions', label: 'Suggestion Universe', icon: Compass },
    { id: 'dna', label: 'Project DNA', icon: Palette },
    { id: 'guardian', label: 'NEXORAX Guardian', icon: ShieldAlert },
    { id: 'radar', label: 'Future Radar', icon: Crosshair },
    { id: 'bundle', label: 'Launch Bundle Offer', icon: Tag, badge: project && (project as any).bundleApplied ? 'Locked' : 'Save £160' },
    { id: 'projects', label: 'My Projects List', icon: FolderOpen },
    { id: 'versions', label: 'History Revisions', icon: History },
    { id: 'publish', label: 'Publish / Deploy', icon: Server },
    ...(isAdmin ? [{ id: 'adminDbAudit' as BuilderTab, label: 'Admin Security Ledger', icon: Lock, badge: 'Active' }] : [])
  ];

  return (
    <div id="npx-builder-workspace-root" className="bg-[#020512] border border-white/5 rounded-3xl p-6 space-y-6 text-[#f4f4f5] max-w-[1500px] w-full mx-auto">
      
      {/* 1. MASTER WORKSPACE MAIN HEADER */}
      <div className="flex flex-wrap items-center justify-between border-b border-white/5 pb-4.5 gap-4">
        <div className="flex items-center gap-3 text-left">
          <div className="p-3 bg-gradient-to-br from-[#0c242e] to-[#250d32] text-cyan-300 rounded-2xl border border-cyan-500/10">
            <Wand2 className="w-5 h-5 animate-pulse text-cyan-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-sans font-black text-white uppercase tracking-tight">NEXORAX Product Builder</h1>
              <span className="text-[7px] font-mono bg-cyan-950/60 text-[#00ffff] px-1.5 py-0.5 rounded uppercase font-black tracking-widest border border-cyan-500/15">V.0.8S</span>
            </div>
            <p className="text-[10.5px] text-zinc-400 font-sans normal-case leading-none mt-1">Prompt-free secure customisation console. Built entirely as an isolated Nexus OS application.</p>
          </div>
        </div>

        <button
          onClick={onBackToTemplates}
          className="px-4 py-2 bg-zinc-950/60 hover:bg-zinc-900 border border-zinc-850 hover:border-zinc-700 text-zinc-350 font-mono text-[10px] font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Exit Workspace</span>
        </button>
      </div>

      {/* 2. BODY CONTROL PANELS LAYOUT GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* SIDEBAR: SECTIONS NAVIGATION MENU */}
        <div className="lg:col-span-3 bg-zinc-950/40 p-3 rounded-2xl border border-white/5 space-y-3.5">
          <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-widest block font-bold px-2">Navigation Matrix</span>
          
          <div className="space-y-1">
            {menuItems.map((item) => {
              const active = activeTab === item.id;
              // If no project is active, restrict navigation tabs to Launchpad/Projects/AdminDbAudit/DeliveryHub List
              const disabled = !project && item.id !== 'launchpad' && item.id !== 'projects' && item.id !== 'adminDbAudit' && item.id !== 'deliveryHub';

              return (
                <button
                  key={item.id}
                  onClick={() => !disabled && setActiveTab(item.id)}
                  disabled={disabled}
                  className={`w-full p-2.5 rounded-xl text-[10px] font-mono uppercase tracking-wider transition-all flex items-center justify-between text-left group border ${active ? 'bg-zinc-900 border-cyan-500/20 text-[#00ffff] font-black' : disabled ? 'text-zinc-700 cursor-not-allowed border-transparent' : 'text-zinc-450 hover:text-white border-transparent hover:bg-black/20'}`}
                >
                  <div className="flex items-center gap-2">
                    <item.icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-[#00ffff]' : 'text-zinc-500 group-hover:text-white transition-colors'}`} />
                    <span className="truncate">{item.label}</span>
                  </div>
                  {item.badge && (
                    <span className={`text-[7px] font-bold uppercase px-1.5 py-0.5 rounded font-sans shrink-0 leading-none ${item.badge === 'Locked' ? 'bg-[#00ff00]/10 text-[#00ff00]' : 'bg-amber-500/10 text-amber-500'}`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {project && (
            <div className="pt-3 border-t border-white/5 space-y-2">
              <div className="p-2.5 bg-black/40 rounded-xl border border-zinc-900 text-left font-mono text-[9px] space-y-1.5 leading-none">
                <span className="text-[7.5px] text-[#00ffff] font-extrabold uppercase tracking-widest block leading-none mb-1">Active Layout</span>
                <div className="truncate text-white font-bold">{project.name}</div>
                <div className="text-zinc-500 text-[8px] uppercase">{project.projectType}</div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={saveDraft}
                  disabled={saving}
                  className="py-1.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded-lg text-[8.5px] font-mono uppercase text-center border border-white/5 font-bold cursor-pointer transition-colors"
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  type="button"
                  onClick={dispatchSetupRequest}
                  className="py-1.5 bg-gradient-to-r from-cyan-900 to-purple-950 hover:from-cyan-800 hover:to-purple-900 text-[#00ffff] rounded-lg text-[8.5px] font-mono uppercase text-center border border-cyan-500/20 font-black cursor-pointer transition-colors"
                >
                  Request Setup
                </button>
              </div>
            </div>
          )}
        </div>

        {/* CONTENT: PORTAL COMPONENT RENDERS */}
        <div className="lg:col-span-9 bg-zinc-950/15 border border-white/5 p-5 rounded-2xl min-h-[480px] flex flex-col justify-start">
          {activeTab === 'launchpad' && (
            <Launchpad
              presetSwatches={COLOR_SWATCHES}
              onAddLog={onAddLog}
              onStartProject={(p) => {
                setProject(p);
                setActiveTab('sandbox');
              }}
            />
          )}

          {activeTab === 'sandbox' && project && (
            <Sandbox
              project={project}
              onUpdateProject={setProject}
              onAddLog={onAddLog}
            />
          )}

          {activeTab === 'suggestions' && project && (
            <Suggestions
              project={project}
              onUpdateProject={setProject}
              onAddLog={onAddLog}
            />
          )}

          {activeTab === 'dna' && project && (
            <ProjectDNA
              project={project}
              onUpdateProject={setProject}
              presetSwatches={COLOR_SWATCHES}
              onAddLog={onAddLog}
            />
          )}

          {activeTab === 'guardian' && project && (
            <Guardian
              project={project}
              onAddLog={onAddLog}
            />
          )}

          {activeTab === 'radar' && project && (
            <Radar
              project={project}
              onUpdateProject={setProject}
              onAddLog={onAddLog}
            />
          )}

          {activeTab === 'bundle' && project && (
            <Bundle
              project={project}
              onUpdateProject={setProject}
              onAddLog={onAddLog}
            />
          )}

          {activeTab === 'projects' && (
            <MyProjects
              activeProjectId={project?.id || ''}
              onLoadProject={(p) => {
                setProject(p);
                setActiveTab('sandbox');
              }}
              onAddLog={onAddLog}
            />
          )}

          {activeTab === 'versions' && project && (
            <Versions
              project={project}
              onUpdateProject={setProject}
              onAddLog={onAddLog}
            />
          )}

          {activeTab === 'publish' && project && (
            <Publish
              project={project}
              onUpdateProject={setProject}
              onAddLog={onAddLog}
            />
          )}

          {activeTab === 'adminDbAudit' && (
            <AdminDbAudit onAddLog={onAddLog} />
          )}

          {activeTab === 'deliveryHub' && (
            <DeliveryHub 
              project={project} 
              onUpdateProject={setProject} 
              onAddLog={onAddLog} 
            />
          )}
        </div>

      </div>

      {/* SETUP CONFIRMED MODAL */}
      {showSetupConfirm && project && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in font-mono">
          <div className="bg-[#020512] border border-cyan-500/20 max-w-md w-full rounded-2xl p-6 space-y-4 text-left shadow-[0_0_50px_rgba(0,255,255,0.15)] relative">
            <div className="flex items-center gap-2.5 text-cyan-400 border-b border-white/5 pb-3">
              <Sparkles className="w-5 h-5 animate-spin" />
              <h3 className="text-sm font-black uppercase text-white tracking-widest">Setup Mandate Dispatched</h3>
            </div>

            <div className="text-xs text-slate-300 leading-relaxed space-y-2 pt-1">
              <p>To the Executive Support Division of NexoraX Digital:</p>
              <p>We have registered your setup request for the customized preview blueprint <strong>"{project.name}"</strong> under the simulated <strong>WorkSuite CRM queue</strong> on the Director's Desk.</p>
              <p className="border border-[#00ffff]/20 bg-[#00ffff]/5 p-2 rounded-lg text-[#00ffff] font-black text-[11px] uppercase">
                🎫 simulated Ticket ID: NX-SET-{Math.floor(Math.random() * 90000 + 10000)}
              </p>
              <p className="text-[10px] text-zinc-450 leading-relaxed font-sans normal-case">
                Placement fees are pre-authorized under the NI: NH448796B billing scheme. Physical operations team notified automatically. Contact active support helpline 08000 622827 for any immediate inquiries.
              </p>
            </div>

            <div className="flex justify-end pt-2 border-t border-white/5">
              <button
                onClick={() => setShowSetupConfirm(false)}
                className="px-5 py-2 bg-gradient-to-r from-cyan-900 to-purple-950 border border-cyan-500/30 text-white rounded-lg text-[10px] font-bold uppercase cursor-pointer"
              >
                Acknowledge Protocol
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
